import React from 'react';

/**
 * ConfirmModal — generic confirmation dialog.
 *
 * Replaces `window.confirm()` calls so the dialog title stays under our
 * control (the browser-native confirm shows the page's hostname, which is
 * the Cloud Run service URL in production and looks awkward to end users).
 *
 * Render conditionally — pass falsy for `open` (or simply don't render the
 * component) to hide. Caller owns the open/closed state.
 *
 * @param {boolean} open       - When false, nothing renders.
 * @param {string}  title      - Dialog title. Defaults to "Console Confirmation".
 * @param {string|React.ReactNode} message - Body content of the dialog.
 * @param {string}  confirmLabel - Label for the confirm button. Defaults to "OK".
 * @param {string|null} cancelLabel - Label for the cancel button. Pass null to
 *                                    render an acknowledgement-only dialog.
 * @param {boolean} danger     - When true, styles the confirm button red.
 * @param {Function} onConfirm - Called when the user clicks the confirm button.
 * @param {Function} onCancel  - Called when the user clicks Cancel, presses
 *                                Escape, or clicks the backdrop.
 */
export default function ConfirmModal({
    open,
    title = 'Console Confirmation',
    message,
    confirmLabel = 'OK',
    cancelLabel = 'Cancel',
    danger = false,
    onConfirm,
    onCancel,
}) {
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onCancel]);

    if (!open) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onCancel}>
            <div style={{
                background: '#fff', borderRadius: 8, padding: 24, maxWidth: 420,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#3C4043' }}>{title}</h3>
                <div style={{ margin: '0 0 20px', fontSize: 14, color: '#5F6368', lineHeight: 1.5 }}>
                    {message}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {cancelLabel != null && (
                        <button onClick={onCancel} style={{
                            padding: '6px 16px', border: '1px solid #DADCE0', borderRadius: 4,
                            background: '#fff', color: '#3C4043', cursor: 'pointer', fontSize: 13,
                        }}>{cancelLabel}</button>
                    )}
                    <button onClick={onConfirm} autoFocus style={{
                        padding: '6px 16px', border: 'none', borderRadius: 4,
                        background: danger ? '#D32F2F' : '#1967D2', color: '#fff',
                        cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}
